import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dropdown,
  FormField,
  InlineAlert,
  employeeID,
  companyID, // import your app's company_Id helper
} from "../../Common/UIComponents";
import {
  EmailConfig_CreateUpdateEmailTemplate,
  EmailConfig_GetDataForModuleList,
  EmailConfig_GetModuleFunctionalityList,
  EmailConfig_GetTemplateColumnByIDs,
} from "../../API_Service/EmailTemplate_Slice";

import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

/** Normalise whatever shape the API returns into a flat array */
const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const mapDDL = (arr) =>
  (Array.isArray(arr) ? arr : []).map((item) => ({
    label:
      item.Text ?? item.text ?? item.label ?? String(item.Value ?? item.value),
    value: item.Value ?? item.value,
    isAssigned: item.IsAssigned ?? item.isAssigned ?? false,
  }));

// ─────────────────────────────────────────────
// RICH TEXT EDITOR
// ─────────────────────────────────────────────

const RichEditor = ({ value, onChange, onFocus, placeholder, error }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!editorRef.current) return;
    // Always set on first render or when value comes in from outside (edit mode)
    // Only skip if the user is actively editing (isFocused)
    if (isFirstRender.current || !isFocused) {
      if (editorRef.current.innerHTML !== (value || "")) {
        editorRef.current.innerHTML = value || "";
      }
      isFirstRender.current = false;
    }
  }, [value, isFocused]);

  const execCmd = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    emitChange();
  };

  const emitChange = () => onChange?.(editorRef.current?.innerHTML ?? "");

  const insertHTML = (html) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    emitChange();
  };

  useEffect(() => {
    if (editorRef.current) editorRef.current.__insertHTML = insertHTML;
  });

  const TOOLBAR = [
    { cmd: "bold", icon: "format_bold", title: "Bold" },
    { cmd: "italic", icon: "format_italic", title: "Italic" },
    { cmd: "underline", icon: "format_underlined", title: "Underline" },
    null,
    {
      cmd: "insertUnorderedList",
      icon: "format_list_bulleted",
      title: "Bullet list",
    },
    {
      cmd: "insertOrderedList",
      icon: "format_list_numbered",
      title: "Numbered list",
    },
    null,
    { cmd: "justifyLeft", icon: "format_align_left", title: "Align left" },
    {
      cmd: "justifyCenter",
      icon: "format_align_center",
      title: "Align center",
    },
    { cmd: "justifyRight", icon: "format_align_right", title: "Align right" },
  ];

  return (
    <div>
      <div
        className={`flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-[#f1f4f4] rounded-t-xl
        border-b border-[#bdc9c9]/30 transition-colors
        ${isFocused ? "bg-white" : ""}
        ${error ? "border-[#ba1a1a]" : ""}`}
      >
        {TOOLBAR.map((btn, i) =>
          btn === null ? (
            <span key={i} className="w-px h-5 bg-[#bdc9c9]/50 mx-1" />
          ) : (
            <button
              key={btn.cmd}
              type="button"
              title={btn.title}
              onMouseDown={(e) => {
                e.preventDefault();
                execCmd(btn.cmd);
              }}
              className="p-1.5 rounded-lg text-[#3e494a] hover:text-[#08757c] hover:bg-[#e5e9e9]
                transition-all focus-visible:ring-2 focus-visible:ring-[#08757c] focus-visible:outline-none"
            >
              <span className="material-symbols-outlined text-[18px]">
                {btn.icon}
              </span>
            </button>
          ),
        )}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-email-body-editor
        data-placeholder={placeholder || "Compose your email body here…"}
        onInput={emitChange}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
        }}
        onBlur={() => setIsFocused(false)}
        className={`w-full min-h-[200px] max-h-[320px] overflow-y-auto px-4 py-3
          bg-[#e5e9e9] rounded-b-xl outline-none text-sm text-[#181c1d]
          focus:bg-white focus:ring-2 transition-all
          [&_table]:border-collapse [&_table]:w-auto
          [&_td]:border [&_td]:border-gray-400 [&_td]:px-2 [&_td]:py-1
          [&_th]:border [&_th]:border-gray-400 [&_th]:px-2 [&_th]:py-1
          [&:empty]:before:content-[attr(data-placeholder)]
          [&:empty]:before:text-[#3e494a]/40
          ${isFocused ? "bg-white ring-2 ring-[#08757c]" : ""}
          ${error ? "ring-2 ring-[#ba1a1a]" : ""}`}
      />
      {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// PLACEHOLDER PANEL
// ─────────────────────────────────────────────

const PlaceholderPanel = ({
  placeholders,
  moduleName,
  onInsertSubject,
  onInsertBody,
}) => {
  const [activeTab, setActiveTab] = useState("subject");

  return (
    <div className="bg-white rounded-2xl border border-[#e5e9e9] overflow-hidden h-full flex flex-col">
      {" "}
      <div className="px-5 py-4 border-b border-[#e5e9e9]">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[#08757c] text-[18px]">
            data_object
          </span>
          <h4 className="text-sm font-bold text-[#181c1d]">Placeholders</h4>
        </div>
        <p className="text-xs text-[#3e494a]/60 mt-0.5">
          Click a placeholder to insert it at your cursor.
        </p>
      </div>
      <div className="flex border-b border-[#e5e9e9]">
        {[
          { key: "subject", label: "Subject" },
          { key: "body", label: "Body" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors
              ${
                activeTab === tab.key
                  ? "text-[#08757c] border-b-2 border-[#08757c] -mb-px bg-[#e8f4f5]"
                  : "text-[#3e494a]/60 hover:text-[#3e494a]"
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {placeholders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-center">
            <span className="material-symbols-outlined text-[28px] text-[#bdc9c9] mb-1">
              data_object
            </span>
            <p className="text-xs text-[#3e494a]/50">
              Select a functionality to
              <br />
              load placeholders.
            </p>
          </div>
        ) : (
          placeholders.map((ph, i) => (
            <button
              key={i}
              type="button"
              onClick={() =>
                activeTab === "subject"
                  ? onInsertSubject(`{${moduleName}.${ph.value}}`)
                  : onInsertBody(`{${moduleName}.${ph.value}}`)
              }
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left
                bg-[#f7fafa] hover:bg-[#e8f4f5] hover:text-[#08757c]
                border border-transparent hover:border-[#08757c]/20
                transition-all group"
            >
              <span
                className="material-symbols-outlined text-[14px] text-[#3e494a]/50
                group-hover:text-[#08757c] flex-shrink-0"
              >
                drag_indicator
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#181c1d] truncate group-hover:text-[#08757c]">
                  {ph.label}
                </p>
                <p className="text-[10px] text-[#3e494a]/50 font-mono truncate">
                  {`{${moduleName}.${ph.value}}`}
                </p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN CREATE / EDIT FORM
// ─────────────────────────────────────────────

function EmailTemplateCreate({ Data: DataProp, clearForm, onSaveSuccess }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const Data = DataProp ?? state?.templateData ?? null;
  const [isInitializing, setIsInitializing] = useState(!!Data);
  const handleBack = () => navigate("/email-templateindex");

  const subjectRef = useRef(null);

  // ── Local state ──────────────────────────────
  const [moduleID, setModuleID] = useState("");
  const [moduleOptions, setModuleOptions] = useState([]);
  const [functionalityOptions, setFunctionalityOptions] = useState([]);
  const [placeholderData, setPlaceholderData] = useState([]);
  const [isSignatureApplicable, setIsSignatureApplicable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadingFunc, setLoadingFunc] = useState(false);
  const [loadingPH, setLoadingPH] = useState(false);

  const [form, setForm] = useState({
    EmailModuleFunctionalityID: "",
    CCDistributorIDs: [],
    Subject: "",
    Body: "",
  });
  const [errors, setErrors] = useState({});

  const selectedModuleName =
    moduleOptions.find((m) => String(m.value) === String(moduleID))?.label ??
    "";

  // ── Load functionality dropdown ──────────────
  const loadFunctionalities = useCallback(
    async (modID, emailTemplateID = 0, currentFuncID = null) => {
      if (!modID) {
        setFunctionalityOptions([]);
        return;
      }
      setLoadingFunc(true);
      try {
        const res = await dispatch(
          EmailConfig_GetModuleFunctionalityList({
            moduleID: modID,
            templateID: emailTemplateID,
            companyId: companyID,
          }),
        );
        const raw =
          res?.payload?.data?.Data ??
          res?.payload?.Data ??
          res?.payload?.data ??
          res?.payload ??
          {};
        const ddl =
          raw?.DDLModuleFunctionalityList ??
          raw?.ddlModuleFunctionalityList ??
          [];

        const mapped = mapDDL(ddl).map((opt) => ({
          ...opt,
          // never block the functionality this template already owns
          isAssigned:
            opt.isAssigned && String(opt.value) !== String(currentFuncID),
        }));

        setFunctionalityOptions(mapped);
      } catch {
        setFunctionalityOptions([]);
      } finally {
        setLoadingFunc(false);
      }
    },
    [dispatch],
  );

  // ── Load placeholder columns ─────────────────
  const loadPlaceholders = useCallback(
    async (funcID) => {
      if (!funcID) {
        setPlaceholderData([]);
        return;
      }
      setLoadingPH(true);
      try {
        const res = await dispatch(
          EmailConfig_GetTemplateColumnByIDs({
            moduleFunctionID: funcID,
            companyId: companyID,
          }),
        );
        const raw =
          res?.payload?.data?.Data ??
          res?.payload?.Data ??
          res?.payload?.data ??
          res?.payload ??
          {};
        const ddl = raw?.DDLTempColList ?? raw?.ddlTempColList ?? [];
        setPlaceholderData(mapDDL(ddl));
      } catch {
        setPlaceholderData([]);
      } finally {
        setLoadingPH(false);
      }
    },
    [dispatch],
  );

  // ── Load module dropdown + pre-fill (mirrors MVC Index action exactly) ──
  useEffect(() => {
    (async () => {
      try {
        if (!Data) {
          // ── ADD MODE — pass moduleID=0
          const res = await dispatch(
            EmailConfig_GetDataForModuleList({
              moduleID: 0,
              companyId: companyID,
            }),
          );
          const raw =
            res?.payload?.data?.Data ??
            res?.payload?.Data ??
            res?.payload?.data ??
            res?.payload ??
            {};
          setModuleOptions(
            mapDDL(raw?.DDLModuleList ?? raw?.ddlModuleList ?? []),
          );
        } else {
          setIsInitializing(true);
          const templateId = Data.EmailTemplateID ?? 0;
          const emFuncId = Data.ModuleFunctionalityID;

          console.log("EDIT — TemplateID:", templateId, "EmFuncId:", emFuncId);

          // Step 1 — ONE call returns all 3 DDLs
          const res = await dispatch(
            EmailConfig_GetDataForModuleList({
              moduleID: templateId,
              companyId: companyID,
            }),
          );
          // API: { Status, Data: {...} }  wrapped by Axios as  { data: { Status, Data } }
          const raw =
            res?.payload?.data?.Data ?? // ✅ Axios-wrapped: payload.data.Data
            res?.payload?.Data ?? // ✅ direct: payload.Data
            res?.payload?.data ?? // fallback
            res?.payload ??
            {};

          const moduleDDL = mapDDL(
            raw?.DDLModuleList ?? raw?.ddlModuleList ?? [],
          );
          const funcDDL = mapDDL(
            raw?.DDLModuleFunctionalityList ??
              raw?.ddlModuleFunctionalityList ??
              [],
          );
          const colDDL = mapDDL(
            raw?.DDLTempColList ?? raw?.ddlTempColList ?? [],
          );

          setModuleOptions(moduleDDL);
          setFunctionalityOptions(funcDDL);
          setPlaceholderData(colDDL);

          // Step 2 — set moduleID from DDLModuleList first value (the real Module_Id)
          // DDLModuleList returns only the module for this template e.g. { Text: "Holiday", Value: "3184" }
          const resolvedModuleID = String(moduleDDL[0]?.value ?? ""); // ✅ "3184" from response
          setModuleID(resolvedModuleID);

          // Step 3 — fill form fields
          setIsSignatureApplicable(!!Data.IsSignature);
          setForm({
            EmailModuleFunctionalityID: String(
              Data.ModuleFunctionalityID ?? funcDDL[0]?.value ?? "",
            ), // ✅ "4697"
            CCDistributorIDs: Data.CCDistributorIDs
              ? String(Data.CCDistributorIDs).split(",").filter(Boolean)
              : [],
            Subject: Data.Subject ?? "",
            Body: Data.Body ?? "",
          });
          setIsInitializing(false);
        }
      } catch (err) {
        console.error("Init load error:", err);
      }
    })();
  }, [Data, dispatch, loadFunctionalities, loadPlaceholders]);

  // ── Field handlers ───────────────────────────
  const handleModuleChange = useCallback(
    (val) => {
      setModuleID(val);
      setFunctionalityOptions([]);
      setPlaceholderData([]);
      setForm((p) => ({ ...p, EmailModuleFunctionalityID: "" }));
      setErrors((p) => ({
        ...p,
        EmailModule: "",
        EmailModuleFunctionalityID: "",
      }));
      loadFunctionalities(
        val,
        Data?.EmailTemplateID ?? 0,
        form.EmailModuleFunctionalityID,
      );
    },
    [loadFunctionalities, Data],
  );

  const handleFunctionalityChange = useCallback(
    (val) => {
      setForm((p) => ({ ...p, EmailModuleFunctionalityID: String(val) }));
      setErrors((p) => ({ ...p, EmailModuleFunctionalityID: "" }));
      loadPlaceholders(val);
    },
    [loadPlaceholders],
  );

  const isBodyEmpty = (html) => {
    if (!html) return true;
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = (div.textContent || "").replace(/\u00A0/g, " ").trim();
    const hasMedia = /<img|<table/i.test(html); // treat images/tables as non-empty content
    return text.length === 0 && !hasMedia;
  };

  const handleField = useCallback(
    (key) => (val) => {
      const value = val?.target ? val.target.value : val;
      setForm((p) => ({ ...p, [key]: value }));
      setErrors((p) => ({ ...p, [key]: "" }));
    },
    [],
  );

  // ── Insert placeholder into subject ──────────
  const insertSubjectPlaceholder = useCallback((text) => {
    const input = subjectRef.current;
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const newVal = input.value.slice(0, start) + text + input.value.slice(end);
    setForm((p) => ({ ...p, Subject: newVal }));
    setErrors((p) => ({ ...p, Subject: "" }));
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  }, []);

  // ── Insert placeholder into body editor ──────
  const insertBodyPlaceholder = useCallback((text) => {
    const editorEl = document.querySelector("[data-email-body-editor]");
    if (editorEl?.__insertHTML) editorEl.__insertHTML(text);
  }, []);

  // ── Validation ────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!moduleID) errs.EmailModule = "Email Module is required.";
    if (!form.EmailModuleFunctionalityID)
      errs.EmailModuleFunctionalityID = "Functionality is required.";
    if (!form.Subject?.trim()) errs.Subject = "Subject is required.";
    if (isBodyEmpty(form.Body)) errs.Body = "Body is required.";
    return errs;
  };

  // ── Submit ────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    debugger;
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = {
      EmailTemplateID: Data?.EmailTemplateID ?? 0,
      ModuleFunctionalityID: Number(form.EmailModuleFunctionalityID),
      Subject: form.Subject,
      Body: form.Body,
      CreatedBy: employeeID,
      CompanyId: companyID,
    };

    setIsSaving(true);
    setSaveError(null);
    try {
      const res = await dispatch(
        EmailConfig_CreateUpdateEmailTemplate(payload),
      );
      // API returns { Status: "SUCCESS", Message: "...", Data: <int> }
      console.log("Save res:", res?.payload);
      const status =
        res?.payload?.data?.Status ??
        res?.payload?.data?.status ??
        res?.payload?.Status ??
        res?.payload?.status;

      const savedId = res?.payload?.data?.Data ?? res?.payload?.Data;
      if (status === "SUCCESS" && savedId > 0) {
        navigate("/email-templateindex", {
          state: { refresh: true, saveSuccess: true, isNew: !Data },
        });
      } else if (status === "DUPLICATE_FUNCTIONALITY") {
        setSaveError(
          res?.payload?.data?.Message ??
            res?.payload?.Message ??
            "This functionality is already linked to another email template.",
        );
      } else {
        setSaveError(
          res?.payload?.data?.Message ??
            res?.payload?.Message ??
            "Save failed. Please try again.",
        );
      }
    } catch (err) {
      setSaveError(err?.message ?? "Failed to save template.");
    } finally {
      setIsSaving(false);
    }
  }, [form, moduleID, isSignatureApplicable, Data, dispatch, onSaveSuccess]);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  console.log("moduleID:", moduleID, typeof moduleID);
  console.log("moduleOptions:", moduleOptions);
  console.log(
    "form.EmailModuleFunctionalityID:",
    form.EmailModuleFunctionalityID,
    typeof form.EmailModuleFunctionalityID,
  );
  console.log(Data);

  return (
    <DashboardLayout>
      <div className="px-8 py-8 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        {/* Breadcrumb */}
        {/* Breadcrumb + Header — matches CreateEditUserPage pattern */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 gap-4">
          <div>
            <nav className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              <span
                onClick={handleBack}
                className="cursor-pointer hover:text-[#08757c] transition-colors"
              >
                Settings
              </span>
              <span className="material-symbols-outlined text-[12px]">
                chevron_right
              </span>
              <span
                onClick={handleBack}
                className="cursor-pointer hover:text-[#08757c] transition-colors"
              >
                Email Templates
              </span>
              <span className="material-symbols-outlined text-[12px]">
                chevron_right
              </span>
              <span className="text-[#08757c]">
                {Data ? "Edit Template" : "Add Template"}
              </span>
            </nav>
            <h2 className="text-3xl font-extrabold text-[#181c1d] tracking-tight">
              {Data ? "Edit Email Template" : "Add Email Template"}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={clearForm ?? handleBack}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
            >
              <span className="material-symbols-outlined text-lg">
                arrow_back
              </span>
              Back to List
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="flex items-center gap-2 px-8 py-2.5 rounded-lg bg-[#08757c] text-white font-bold text-sm shadow-sm hover:bg-[#08757c]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-lg">
                {isSaving ? "hourglass_empty" : "save"}
              </span>
              {isSaving
                ? "Saving..."
                : Data
                  ? "Update Template"
                  : "Save Template"}
            </button>
          </div>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40 p-8">
          <div className="grid grid-cols-12 gap-5">
            {/* ── LEFT: form ─────────────────────────── */}
            <div className="col-span-7 space-y-5">
              {/* Email Module */}
              <FormField id="EmailModule" label="Email Module" required>
                {isInitializing ? (
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#e5e9e9] rounded-xl text-sm text-[#3e494a]">
                    <span className="material-symbols-outlined animate-spin text-[#08757c] text-[18px]">
                      progress_activity
                    </span>
                    Loading…
                  </div>
                ) : (
                  <Dropdown
                    options={moduleOptions}
                    value={moduleID}
                    onChange={handleModuleChange}
                    placeholder="Select module…"
                    error={errors.EmailModule}
                  />
                )}
              </FormField>

              {/* Functionality */}
              <FormField
                id="EmailModuleFunctionalityID"
                label="Email Module Functionality"
                required
              >
                {isInitializing ? (
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#e5e9e9] rounded-xl text-sm text-[#3e494a]">
                    <span className="material-symbols-outlined animate-spin text-[#08757c] text-[18px]">
                      progress_activity
                    </span>
                    Loading…
                  </div>
                ) : (
                  <Dropdown
                    options={functionalityOptions.map((o) => ({
                      label: o.isAssigned
                        ? `${o.label} (already in use)`
                        : o.label,
                      value: o.value,
                      disabled: o.isAssigned,
                    }))}
                    value={form.EmailModuleFunctionalityID}
                    onChange={handleFunctionalityChange}
                    placeholder={
                      loadingFunc
                        ? "Loading…"
                        : moduleID
                          ? "Select functionality…"
                          : "Select module first…"
                    }
                    disabled={!moduleID || loadingFunc}
                    error={errors.EmailModuleFunctionalityID}
                  />
                )}
              </FormField>

              {/* Subject */}
              <FormField
                id="Subject"
                label="Subject"
                required
                error={errors.Subject}
              >
                <input
                  ref={subjectRef}
                  id="Subject"
                  type="text"
                  value={form.Subject}
                  onChange={handleField("Subject")}
                  placeholder="Enter email subject…"
                  aria-invalid={!!errors.Subject}
                  className={`block w-full px-4 py-3 bg-[#e5e9e9] border-none rounded-xl
              text-[#181c1d] placeholder:text-[#bdc9c9] outline-none
              focus:bg-white focus:ring-2 transition-all
              ${errors.Subject ? "focus:ring-red-400 ring-2 ring-[#ba1a1a]" : "focus:ring-[#005b60]/20"}`}
                />
              </FormField>
              {/* Body */}
              <FormField id="Body" label="Email Body" required>
                <div>
                  <RichEditor
                    value={form.Body}
                    onChange={handleField("Body")}
                    error={errors.Body}
                    placeholder="Compose the email body here…"
                  />
                </div>
              </FormField>

              {/* Save error */}
              {saveError && <InlineAlert type="error" message={saveError} />}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={clearForm ?? handleBack}
                  disabled={isSaving}
                  className="px-6 py-2.5 text-sm font-bold text-[#3e494a] bg-[#e5e9e9]
              rounded-xl hover:bg-[#d8dcdc] active:scale-95 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white
    text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20
    hover:opacity-90 active:scale-95 transition-all flex items-center gap-2
    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <span className="material-symbols-outlined text-[18px] animate-spin">
                        progress_activity
                      </span>
                      Saving…
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-[18px]">
                        save
                      </span>
                      {Data ? "Update Template" : "Save Template"}{" "}
                      {/* ← fix here */}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ── RIGHT: placeholders ─────────────────── */}
            <div
              className="col-span-5 sticky top-6"
              style={{ height: "calc(100vh - 160px)", maxHeight: "530px" }}
            >
              {loadingPH ? (
                <div className="flex items-center justify-center h-full text-[#3e494a] text-sm gap-2">
                  <span className="material-symbols-outlined animate-spin text-[#08757c]">
                    progress_activity
                  </span>
                  Loading placeholders…
                </div>
              ) : (
                <PlaceholderPanel
                  placeholders={placeholderData}
                  moduleName={selectedModuleName}
                  onInsertSubject={insertSubjectPlaceholder}
                  onInsertBody={insertBodyPlaceholder}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default memo(EmailTemplateCreate);
